// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RequestState } from "@utils/types";
import {
  BusinessUnit,
  CareerFunction,
  Company,
  Employee,
  HrState,
  Recruiter,
} from "./model";

import { createAsyncThunk } from "@reduxjs/toolkit";
import { APIService } from "@utils/apiService";
import { AppConfig } from "@config/config";

export const getLeads = createAsyncThunk("employee/getLeads", async () => {
  return new Promise<any>((resolve, reject) => {
    const url = `${
      AppConfig.serviceUrls.employees
    }?lead=${true}&manager=${false}`;
    APIService.getInstance()
      .get(url)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
});

export const getHrManagers = createAsyncThunk(
  "employee/getHrManagers",
  async () => {
    return new Promise<any>((resolve, reject) => {
      const url = `${
        AppConfig.serviceUrls.employees
      }?lead=${true}&manager=${true}`;
      APIService.getInstance()
        .get(url)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
);

export const getRecruiters = createAsyncThunk(
  "recruiters/getRecruiters",
  async () => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .get(AppConfig.serviceUrls.recruiter)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
);

export const getAllEmployees = createAsyncThunk(
  "employee/getAllEmployees",
  async () => {
    return new Promise<any>((resolve, reject) => {
      const url = `${AppConfig.serviceUrls.employees}`;
      APIService.getInstance()
        .get(url)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
);

export const getCareerFunctions = createAsyncThunk(
  "employee/getCareerFunctions",
  async () => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .get(AppConfig.serviceUrls.careerFunctions)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
);

export const getOrgDetails = createAsyncThunk(
  "employee/getOrgDetails",
  async () => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .get(AppConfig.serviceUrls.orgDetails)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
);

export const getCompanies = createAsyncThunk(
  "employee/getCompanies",
  async () => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .get(AppConfig.serviceUrls.companies)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
);

export const loadInitialData = createAsyncThunk(
  "employee/loadInitialData",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      await Promise.all([
        dispatch(getLeads()).unwrap(),
        dispatch(getRecruiters()).unwrap(),
        dispatch(getHrManagers()).unwrap(),
        dispatch(getAllEmployees()).unwrap(),
        dispatch(getCareerFunctions()).unwrap(),
        dispatch(getOrgDetails()).unwrap(),
        dispatch(getCompanies()).unwrap(),
      ]);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const initialState: HrState = {
  leads: [],
  leadsMap: {},
  employees: [],
  employeesMap: {},
  leadsBasic: [],
  hiringManagers: [],
  careerFunctions: [],
  businessUnits: [],
  companies: [],
  recruiters: [],
  state: RequestState.IDLE,
  overallState: RequestState.IDLE,
};

const employeeSlice = createSlice({
  name: "employee",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(loadInitialData.pending, (state) => {
      state.overallState = RequestState.LOADING;
    });
    builder.addCase(loadInitialData.fulfilled, (state) => {
      state.overallState = RequestState.SUCCEEDED;
    });
    builder.addCase(loadInitialData.rejected, (state) => {
      state.overallState = RequestState.FAILED;
    });

    builder.addCase(getLeads.pending, (state) => {
      state.state = RequestState.LOADING;
    });
    builder.addCase(
      getLeads.fulfilled,
      (state, action: PayloadAction<Employee[]>) => {
        state.leads = action.payload.sort((a,b)=>a.workEmail.localeCompare(b.workEmail)).map((item) => item.workEmail);
        state.leadsMap = action.payload.reduce(
          (acc, lead) => {
            acc[lead.workEmail] = {
              leadName: lead.firstName + " " + lead.lastName,
              leadThumbnail: lead.employeeThumbnail,
            };
            return acc;
          },
          {} as {
            [key: string]: {
              leadName: string;
              leadThumbnail: string;
            };
          }
        );
        state.state = RequestState.SUCCEEDED;
      }
    );
    builder.addCase(getLeads.rejected, (state) => {
      state.state = RequestState.FAILED;
    });

    builder.addCase(getAllEmployees.pending, (state) => {
      state.state = RequestState.LOADING;
    });
    builder.addCase(
      getAllEmployees.fulfilled,
      (state, action: PayloadAction<Employee[]>) => {
        state.employees = action.payload.map((item) => item.workEmail);
        state.employeesMap = action.payload.reduce(
          (acc, employee) => {
            acc[employee.workEmail] = {
              employeeName: employee.firstName + " " + employee.lastName,
              employeeThumbnail: employee.employeeThumbnail,
              employeeEmail: employee.workEmail,
            };
            return acc;
          },
          {} as {
            [key: string]: {
              employeeName: string;
              employeeThumbnail: string;
              employeeEmail: string;
            };
          }
        );
        state.state = RequestState.SUCCEEDED;
      }
    );
    builder.addCase(getAllEmployees.rejected, (state) => {
      state.state = RequestState.FAILED;
    });

    builder.addCase(getHrManagers.pending, (state) => {
      state.state = RequestState.LOADING;
    });
    builder.addCase(getHrManagers.fulfilled, (state, action: PayloadAction<Employee[]>) => {
      state.hiringManagers = action.payload.sort((a,b)=> a.workEmail.localeCompare(b.workEmail)).map((item) => item.workEmail);
      state.state = RequestState.SUCCEEDED;
    });
    builder.addCase(getHrManagers.rejected, (state) => {
      state.state = RequestState.FAILED;
    });

    builder.addCase(getCareerFunctions.pending, (state) => {
      state.state = RequestState.LOADING;
    });
    builder.addCase(getCareerFunctions.fulfilled, (state, action: PayloadAction<CareerFunction[]>) => {
      state.careerFunctions = [...action.payload].sort((a, b) =>  a.careerFunction.localeCompare(b.careerFunction));
      state.state = RequestState.SUCCEEDED;
    });
    builder.addCase(getCareerFunctions.rejected, (state) => {
      state.state = RequestState.FAILED;
    });

    builder.addCase(getOrgDetails.pending, (state) => {
      state.state = RequestState.LOADING;
    });
    builder.addCase(getOrgDetails.fulfilled, (state, action: PayloadAction<BusinessUnit[]>) => {
      state.businessUnits = [...action.payload].sort((a, b) => a.businessUnit.localeCompare(b.businessUnit));
      state.state = RequestState.SUCCEEDED;
    });
    builder.addCase(getOrgDetails.rejected, (state) => {
      state.state = RequestState.FAILED;
    });

    builder.addCase(getCompanies.pending, (state) => {
      state.state = RequestState.LOADING;
    });
    builder.addCase(getCompanies.fulfilled, (state, action: PayloadAction<Company[]>) => {
      state.companies = [...action.payload].sort((a, b) => a.company.localeCompare(b.company));
      state.state = RequestState.SUCCEEDED;
    });
    builder.addCase(getCompanies.rejected, (state) => {
      state.state = RequestState.FAILED;
    });

    builder.addCase(getRecruiters.pending, (state) => {
      state.state = RequestState.LOADING;
    });
    builder.addCase(getRecruiters.fulfilled, (state, action: PayloadAction<Recruiter[]>) => {
      state.recruiters = action.payload.sort((a, b) => a.email.localeCompare(b.email)).map((item) => item.email);
      state.state = RequestState.SUCCEEDED;
    });
    builder.addCase(getRecruiters.rejected, (state) => {
      state.state = RequestState.FAILED;
    });
  },
});

export default employeeSlice.reducer;
